import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { fetchServiceMenuItems, NavigationMenuItem } from '../../services/navigationService';
import { getFlagUrl } from '../../utils/flagUtils';
import { useUserAuth } from '../../contexts/UserAuthContext';

interface Package {
  id: string;
  name: string;
  price: string;
  features: string[];
  description: string;
  popular: boolean;
}

interface CountryPackages {
  id: string;
  name: string;
  flag: string;
  flagEmoji?: string;
  countryCode?: string;
  packages: Package[];
}

interface EnterprisePackagesSectionProps {
  allPackages: Record<string, Package[]>;
}

const EnterprisePackagesSection: React.FC<EnterprisePackagesSectionProps> = ({ allPackages }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useUserAuth();
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [countries, setCountries] = useState<CountryPackages[]>([]);
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  // Fetch dynamic country data from service pages
  useEffect(() => {
    const loadCountries = async () => {
      try {
        const menuItems = await fetchServiceMenuItems(i18n.language);

        // Map menu items to country packages, matching with allPackages
        const dynamicCountries: CountryPackages[] = menuItems.map((item: NavigationMenuItem) => {
          // Extract country ID from slug (e.g., 'saudi-arabia' -> 'saudi')
          const countryId = item.slug;

          return {
            id: countryId,
            name: item.name,
            flag: getFlagUrl(item.country_code),
            flagEmoji: item.fallback,
            countryCode: item.country_code,
            packages: allPackages[countryId] || []
          };
        });

        setCountries(dynamicCountries);
      } catch (error) {        // Fallback to empty array if API fails
        setCountries([]);
      }
    };

    loadCountries();
  }, [i18n.language, allPackages]);

  const handleChoosePackage = async (packageId: string, countryId: string) => {
    if (isAuthenticated) {
      // User is logged in - redirect to requests page with preselected package
      try {
        const { default: apiService } = await import('../../services/api');
        const data = await apiService.getPackage(packageId);

        if (data.success && data.package) {
          const selectedPackage = {
            id: data.package.id,
            name: data.package.name,
            description: data.package.description,
            price: parseFloat(data.package.price),
            currency: data.package.currency,
            features: Array.isArray(data.package.features) ? data.package.features : JSON.parse(data.package.features || '[]'),
            processingTime: data.package.processing_time,
            popular: data.package.popular,
            country: {
              name: data.package.country_name,
              code: data.package.country_code,
              flag: data.package.country_flag || '🌍'
            }
          };

          navigate('/dashboard/requests', {
            state: {
              selectedPackage: selectedPackage,
              message: 'Your selected package is ready to complete.'
            }
          });
        }
      } catch (error) {      }
    } else {
      // User is not logged in - redirect to register page
      navigate('/register', { state: { packageId, country: countryId } });
    }
  };

  const handleShowDetails = (countryId: string) => {
    const serviceRoutes: Record<string, string> = {
      'egypt': '/services/egypt',
      'saudi': '/services/saudi-arabia',
      'usa': '/services/usa',
      'uk': '/services/uk'
    };
    navigate(serviceRoutes[countryId] || `/services/${countryId}`);
    // Scroll to packages section after navigation
    setTimeout(() => {
      const packagesSection = document.querySelector('#packages');
      if (packagesSection) {
        packagesSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 500);
  };

  const scrollToContact = () => {
    navigate('/contact');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2
      }
    }
  };

  const countryCardVariants = {
    hidden: {
      opacity: 0,
      y: 60,
      scale: 0.8
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut" as const
      }
    }
  };

  const packageVariants = {
    hidden: {
      opacity: 0,
      x: -30,
      scale: 0.95
    },
    visible: {
      opacity: 1,
      x: 0,
      scale: 1,
      transition: {
        duration: 0.4,
        ease: "easeOut" as const
      }
    }
  };

  return (
    <section id="packages" className="py-20 bg-gradient-to-br from-gray-50 via-white to-gray-100 relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute top-20 right-20 w-96 h-96 bg-primary-100 rounded-full opacity-20"
          animate={{
            scale: [1, 1.1, 1],
            rotate: [0, 90, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear" as const
          }}
        />
        <motion.div
          className="absolute bottom-20 left-20 w-64 h-64 bg-primary-200 rounded-full opacity-15"
          animate={{
            scale: [1, 0.8, 1],
            rotate: [0, -90, 0],
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "linear" as const
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: -30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <motion.h2
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {t('packages.title', 'Choose Your Perfect Package')}
          </motion.h2>
          <motion.p
            className="text-xl text-gray-600 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            {t('packages.subtitle', 'Professional company formation services tailored for each country')}
          </motion.p>
        </motion.div>

        {/* Countries Grid */}
        <motion.div
          ref={ref}
          className="grid grid-cols-1 lg:grid-cols-2 gap-8"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          {countries.map((country) => (
            <motion.div
              key={country.id}
              className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden hover:shadow-2xl transition-all duration-300"
              variants={countryCardVariants}
              whileHover={{
                scale: 1.02,
                y: -5,
                transition: { duration: 0.2 }
              }}
            >
              {/* Country Header */}
              <div className="bg-gradient-to-r from-primary-600 to-primary-800 p-6 text-white relative overflow-hidden">
                <motion.div
                  className="absolute inset-0 bg-white opacity-10"
                  animate={{
                    x: ['-100%', '100%'],
                  }}
                  transition={{
                    duration: 150,
                    repeat: Infinity,
                    ease: "linear" as const,
                    delay: Math.random() * 20
                  }}
                />
                <div className="flex items-center space-x-4 rtl:space-x-reverse relative z-10">
                  <motion.div
                    className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center overflow-hidden border-2 border-white/30"
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ duration: 0.2 }}
                  >
                    <img
                      src={country.flag}
                      alt={`${country.name} flag`}
                      className="w-12 h-8 object-cover rounded"
                      onError={(e) => {
                        // Fallback to emoji if image fails to load
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        const parent = target.parentNode as HTMLElement;
                        if (parent && !parent.querySelector('.flag-fallback')) {
                          const emojiSpan = document.createElement('span');
                          emojiSpan.className = 'flag-fallback text-2xl';
                          emojiSpan.textContent = country.flagEmoji || '🌍';
                          parent.appendChild(emojiSpan);
                        }
                      }}
                    />
                  </motion.div>
                  <div>
                    <h3 className="text-2xl font-bold">{country.name}</h3>
                    <p className="text-primary-100 opacity-90">
                      {country.packages.length} {t('packages.available', 'packages available')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Packages List */}
              <div className="p-6">
                {country.packages.length > 0 ? (
                  <div className="space-y-4">
                    <AnimatePresence>
                      {country.packages.slice(0, selectedCountry === country.id ? undefined : 3).map((pkg, index) => (
                        <motion.div
                          key={pkg.id}
                          className={`border rounded-xl p-5 transition-all duration-200 hover:shadow-lg ${
                            !!pkg.popular
                              ? 'border-primary-300 bg-primary-50/50'
                              : 'border-gray-200 hover:border-primary-200'
                          }`}
                          variants={packageVariants}
                          initial="hidden"
                          animate="visible"
                          transition={{ delay: index * 0.1 }}
                          layout
                        >
                          {!!pkg.popular && (
                            <motion.div
                              className="inline-block bg-primary-500 text-white text-xs px-3 py-1 rounded-full font-semibold mb-3"
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{ delay: 0.5 }}
                            >
                              {t('packages.popular', 'Most Popular')}
                            </motion.div>
                          )}

                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className="text-lg font-bold text-gray-900 mb-2">{pkg.name}</h4>
                              <p className="text-gray-600 text-sm mb-3 line-clamp-2">{pkg.description}</p>

                              {/* Features Preview */}
                              <div className="flex flex-wrap gap-2 mb-4">
                                {pkg.features.slice(0, 3).map((feature, idx) => (
                                  <motion.span
                                    key={idx}
                                    className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full"
                                    initial={{ opacity: 0, scale: 0.8 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ delay: 0.3 + idx * 0.1 }}
                                  >
                                    ✓ {feature.length > 20 ? feature.substring(0, 20) + '...' : feature}
                                  </motion.span>
                                ))}
                                {pkg.features.length > 3 && (
                                  <span className="text-xs text-primary-600 font-medium">
                                    +{pkg.features.length - 3} {t('packages.more', 'more')}
                                  </span>
                                )}
                              </div>
                            </div>

                            <div className="text-right ml-4 rtl:ml-0 rtl:mr-4">
                              <div className="text-2xl font-bold text-primary-600 mb-2">{pkg.price}</div>
                              <motion.button
                                onClick={() => handleChoosePackage(pkg.id, country.id)}
                                className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-200 ${
                                  !!pkg.popular
                                    ? 'bg-primary-600 text-white hover:bg-primary-700 shadow-md'
                                    : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                                }`}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                              >
                                {t('packages.choose', 'Choose')}
                              </motion.button>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </AnimatePresence>

                    {/* View More/Less Button */}
                    {country.packages.length > 3 && (
                      <motion.button
                        onClick={() => setSelectedCountry(selectedCountry === country.id ? null : country.id)}
                        className="w-full py-3 text-primary-600 font-semibold border-2 border-primary-200 rounded-lg hover:bg-primary-50 transition-all duration-200 mb-3"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        {selectedCountry === country.id
                          ? t('packages.viewLess', 'View Less')
                          : t('packages.viewMore', `View All ${country.packages.length} Packages`)
                        }
                      </motion.button>
                    )}

                    {/* Show Details Button */}
                    <motion.button
                      onClick={() => handleShowDetails(country.id)}
                      className="w-full py-3 text-white font-semibold bg-gradient-to-r from-primary-500 to-primary-600 rounded-lg hover:from-primary-600 hover:to-primary-700 transition-all duration-200 shadow-md"
                      whileHover={{ scale: 1.02, y: -1 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {t('packages.showDetails', 'Show All Packages & Details')} →
                    </motion.button>
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="space-y-4"
                    >
                      <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center">
                        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2 2v-5m16 0h-2M4 13h2m0 0V9a2 2 0 012-2h8a2 2 0 012 2v4" />
                        </svg>
                      </div>
                      <div>
                        <p className="font-medium">{t('errors.noPackagesAvailable')}</p>
                        <p className="text-sm text-gray-400">{t('errors.packagesWillAppear')}</p>
                      </div>
                    </motion.div>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Call to Action */}
        <motion.div
          className="text-center mt-16"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <p className="text-gray-600 mb-6">{t('packages.needHelp', 'Need help choosing the right package?')}</p>
          <motion.button
            onClick={scrollToContact}
            className="bg-primary-600 text-white px-8 py-4 rounded-lg font-semibold text-lg shadow-lg hover:bg-primary-700 transition-all duration-200"
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
          >
            {t('packages.contact', 'Contact Our Experts')}
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default EnterprisePackagesSection;